import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Spinner, Row, Col } from 'react-bootstrap';
import { machineService } from '../services/api';

const KioskControlModal = ({ show, onHide, machine, onSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [kioskConfig, setKioskConfig] = useState({
    url: 'https://www.google.com',
    password: '',
    action: 'start'
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setKioskConfig(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const executeKioskAction = async (action) => {
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      if (action === 'start') {
        await machineService.startKiosk(machine.id, kioskConfig.url, kioskConfig.password);
      } else {
        await machineService.stopKiosk(machine.id, kioskConfig.password);
      }

      setSuccess(`Kiosk ${action} command sent successfully to ${machine.name}!`);
      if (onSuccess) onSuccess();
      
      setTimeout(() => {
        onHide();
        setSuccess('');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${action} kiosk mode`);
    } finally {
      setIsLoading(false);
    }
  };

  const getPresetUrls = () => [
    { name: 'Google Search', url: 'https://www.google.com' },
    { name: 'YouTube TV', url: 'https://www.youtube.com' },
    { name: 'Infrastructure UI', url: 'http://localhost:3000' },
    { name: 'Default Portal', url: 'chrome://newtab/' }
  ];

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <div className="modal-content-premium">
        <Modal.Header closeButton className="modal-header-premium border-0">
          <Modal.Title style={{ fontWeight: 700 }}>
            <i className="fas fa-desktop me-2 text-primary"></i>
            Kiosk Mode Control - {machine?.name}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="px-4 py-3">
          {error && (
            <Alert variant="danger" className="border-0 mb-3" style={{ background: 'rgba(244, 63, 94, 0.15)', color: '#fb7185' }}>
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error}
            </Alert>
          )}

          {success && (
            <Alert variant="success" className="border-0 mb-3" style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#34d399' }}>
              <i className="fas fa-check-circle me-2"></i>
              {success}
            </Alert>
          )}

          <Form onSubmit={(e) => { e.preventDefault(); executeKioskAction(kioskConfig.action); }}>
            {/* Action Selector */}
            <Form.Group className="mb-3">
              <Form.Label className="form-label-premium text-uppercase tracking-wider">Select Action</Form.Label>
              <Form.Select
                name="action"
                value={kioskConfig.action}
                onChange={handleInputChange}
                className="form-input-premium w-100"
                disabled={isLoading}
              >
                <option value="start">Start Fullscreen Kiosk Mode</option>
                <option value="stop">Stop Kiosk Mode & Restore Desktop</option>
              </Form.Select>
            </Form.Group>

            {/* URL config (Only for start) */}
            {kioskConfig.action === 'start' && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label-premium text-uppercase tracking-wider">Kiosk Destination URL</Form.Label>
                  <Form.Control
                    type="url"
                    name="url"
                    value={kioskConfig.url}
                    onChange={handleInputChange}
                    className="form-input-premium w-100"
                    placeholder="https://example.com"
                    disabled={isLoading}
                    required
                  />
                  <Form.Text className="text-muted" style={{ fontSize: '0.75rem' }}>
                    Chrome will spin up in fullscreen mode locked to this address.
                  </Form.Text>
                </Form.Group>

                {/* Preset URLs */}
                <Form.Group className="mb-3.5">
                  <Form.Label className="form-label-premium text-uppercase tracking-wider">Preset Templates</Form.Label>
                  <Row className="g-2">
                    {getPresetUrls().map((preset, index) => (
                      <Col xs={6} key={index}>
                        <Button
                          variant="outline-secondary"
                          className="btn-premium-secondary w-100 text-start py-1.5 px-3"
                          style={{ fontSize: '0.8rem' }}
                          onClick={() => setKioskConfig(prev => ({
                            ...prev,
                            url: preset.url
                          }))}
                          disabled={isLoading}
                        >
                          <i className="fas fa-link me-2 text-primary"></i>
                          {preset.name}
                        </Button>
                      </Col>
                    ))}
                  </Row>
                </Form.Group>
              </>
            )}

            {/* Password Auth */}
            <Form.Group className="mb-4">
              <Form.Label className="form-label-premium text-uppercase tracking-wider">Credential Override Key</Form.Label>
              <Form.Control
                type="password"
                name="password"
                value={kioskConfig.password}
                onChange={handleInputChange}
                className="form-input-premium w-100"
                placeholder="Enter password override (leave blank to use database password)"
                disabled={isLoading}
              />
            </Form.Group>

            {/* Action Explanation Info */}
            <div className="p-3 mb-2 rounded-lg" style={{ background: 'rgba(120, 120, 120, 0.04)', border: '1px solid var(--border-color)' }}>
              <h6 className="text-theme-main mb-2" style={{ fontWeight: 700, fontSize: '0.85rem' }}>
                <i className="fas fa-info-circle me-1.5 text-primary"></i>
                Orchestrator Execution:
              </h6>
              {kioskConfig.action === 'start' ? (
                <ul className="mb-0 text-theme-muted" style={{ paddingLeft: '1.25rem', fontSize: '0.8rem' }}>
                  <li>Remotely inject registry adjustments to block Alt+Tab, Win keys, Close Explorer</li>
                  <li>Disable remote keyboard exits (Task Manager lockouts)</li>
                  <li>Execute Chrome in fullscreen `--kiosk` locked to destination URL</li>
                </ul>
              ) : (
                <ul className="mb-0 text-theme-muted" style={{ paddingLeft: '1.25rem', fontSize: '0.8rem' }}>
                  <li>Re-enable hotkeys, local window operations, and task managers</li>
                  <li>Perform remote termination (`taskkill`) of Chrome instances</li>
                </ul>
              )}
            </div>
          </Form>
        </Modal.Body>

        <Modal.Footer className="modal-footer-premium border-0 gap-2">
          <Button className="btn-premium-secondary" onClick={onHide} disabled={isLoading}>
            Cancel
          </Button>
          <Button 
            className={kioskConfig.action === 'start' ? 'btn-premium-primary' : 'btn-premium-accent'}
            onClick={() => executeKioskAction(kioskConfig.action)}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Executing...
              </>
            ) : (
              <>
                <i className={`fas ${kioskConfig.action === 'start' ? 'fa-play' : 'fa-stop'} me-2`}></i>
                {kioskConfig.action === 'start' ? 'Launch Kiosk' : 'Halt Kiosk'}
              </>
            )}
          </Button>
        </Modal.Footer>
      </div>
    </Modal>
  );
};

export default KioskControlModal;